import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ProposalDetail = () => {
  const { id } = useParams();
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertData, setConvertData] = useState({
    client_id: '',
    assigned_to: '',
    scheduled_date: '',
    scheduled_time: ''
  });
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchProposal();
    if (isAdmin) {
      fetchClients();
      fetchUsers();
    }
  }, [id]);

  const fetchProposal = async () => {
    try {
      const response = await fetch(`${API_URL}/proposals/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch proposal');
      const data = await response.json();
      setProposal(data);
    } catch (error) {
      console.error('Error fetching proposal:', error);
      toast.error('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this proposal?`)) return;

    try {
      const response = await fetch(`${API_URL}/proposals/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success(`Proposal ${newStatus}`);
      fetchProposal();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/proposals/${id}/convert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(convertData)
      });

      if (!response.ok) throw new Error('Failed to convert proposal');
      const data = await response.json();
      toast.success('Proposal converted to service call!');
      navigate(`/service-calls`);
    } catch (error) {
      console.error('Error converting proposal:', error);
      toast.error('Failed to convert proposal');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this proposal? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/proposals/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete proposal');
      toast.success('Proposal deleted');
      navigate('/proposals');
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: '#f3f4f6', color: '#6b7280' },
      under_review: { bg: '#fef3c7', color: '#92400e' },
      approved: { bg: '#d1fae5', color: '#065f46' },
      rejected: { bg: '#fee2e2', color: '#991b1b' },
      converted: { bg: '#dbeafe', color: '#1e40af' }
    };
    const style = styles[status] || styles.draft;
    return (
      <span style={{
        padding: '6px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        background: style.bg,
        color: style.color,
        textTransform: 'uppercase'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!proposal) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Proposal not found</div>;
  }

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/proposals')}
            style={{
              padding: '8px',
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex'
            }}
          >
            <ArrowLeftIcon style={{ width: 20, height: 20 }} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                {proposal.client_name}
              </h1>
              {getStatusBadge(proposal.status)}
            </div>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Submitted {new Date(proposal.submitted_at).toLocaleDateString()}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {proposal.status === 'draft' && (
            <button
              onClick={() => navigate(`/proposals/edit/${id}`)}
              style={{
                padding: '10px 16px',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <PencilIcon style={{ width: 16, height: 16 }} /> Edit
            </button>
          )}
          {isAdmin && proposal.status === 'under_review' && (
            <>
              <button
                onClick={() => handleStatusChange('approved')}
                style={{
                  padding: '10px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <CheckCircleIcon style={{ width: 16, height: 16 }} /> Approve
              </button>
              <button
                onClick={() => handleStatusChange('rejected')}
                style={{
                  padding: '10px 16px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <XCircleIcon style={{ width: 16, height: 16 }} /> Reject
              </button>
            </>
          )}
          {isAdmin && proposal.status === 'approved' && (
            <button
              onClick={() => setShowConvertModal(true)}
              style={{
                padding: '10px 16px',
                background: '#2B7FE0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <ArrowPathIcon style={{ width: 16, height: 16 }} /> Convert to Service Call
            </button>
          )}
          {isAdmin && (
            <button
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <TrashIcon style={{ width: 16, height: 16 }} /> Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gap: '24px' }}>
        {/* Client Information */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Client Information</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {proposal.client_phone && (
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Phone</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>{proposal.client_phone}</div>
              </div>
            )}
            {proposal.client_email && (
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Email</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>{proposal.client_email}</div>
              </div>
            )}
            {proposal.client_address && (
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Address</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {proposal.client_address}
                  {(proposal.client_city || proposal.client_state || proposal.client_zip) && (
                    <div>{proposal.client_city}{proposal.client_state && `, ${proposal.client_state}`} {proposal.client_zip}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Details */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Project Details</h2>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Description</div>
            <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{proposal.project_description}</div>
          </div>

          {proposal.estimated_scope && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Estimated Scope</div>
              <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{proposal.estimated_scope}</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            {proposal.estimated_cost && (
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Estimated Cost</div>
                <div style={{ fontSize: '20px', fontWeight: '600', color: '#2B7FE0' }}>
                  ${parseFloat(proposal.estimated_cost).toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {proposal.notes && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Notes</div>
              <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{proposal.notes}</div>
            </div>
          )}
        </div>

        {/* Photos */}
        {proposal.photos && proposal.photos.length > 0 && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Photos ({proposal.photos.length})</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
              {proposal.photos.map(photo => (
                <div key={photo.id} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  <img
                    src={photo.photo_url}
                    alt="Proposal"
                    style={{ width: '100%', height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => window.open(photo.photo_url, '_blank')}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Convert Modal */}
      {showConvertModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Convert to Service Call</h2>
            
            <form onSubmit={handleConvert}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Client *</label>
                <select
                  required
                  value={convertData.client_id}
                  onChange={(e) => setConvertData({...convertData, client_id: e.target.value})}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="">Select client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Assign To</label>
                <select
                  value={convertData.assigned_to}
                  onChange={(e) => setConvertData({...convertData, assigned_to: e.target.value})}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Date *</label>
                  <input
                    type="date"
                    required
                    value={convertData.scheduled_date}
                    onChange={(e) => setConvertData({...convertData, scheduled_date: e.target.value})}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Time</label>
                  <input
                    type="time"
                    value={convertData.scheduled_time}
                    onChange={(e) => setConvertData({...convertData, scheduled_time: e.target.value})}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowConvertModal(false)}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', fontSize: '14px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '8px 16px', background: '#2B7FE0', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                >
                  Convert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalDetail;